import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Input,
  message as antMessage,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  MailOutlined,
  ReloadOutlined,
  StopOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { HomeIcon } from "lucide-react";
import {
  deleteData,
  fetchData,
  get409Detail,
  is409WithCode,
  postResponseData,
  updateData,
} from "../../api/apiService";
import { USERS } from "../../api/endpointMap";
import PageTitle from "../../components/PageTitle";
import TenantLink from "../../components/TenantLink";
import { useAuth } from "../../context/AuthContext";
import { useEmbedToken } from "../../context/EmbedTokenContext";
import { useEmbedMode } from "../../layouts/EmbedLayout";
import { useOrgUsers } from "../../context/OrgUsersContext";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { formatDate } from "../admin/utils";
import InviteUserModal from "../../components/InviteUserModal";
import UserDeactivationModal from "../../components/UserDeactivationModal";
import PortalAccessFields from "../../components/PortalAccessFields";
import { useFeatureEnabled } from "../../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../../utils/featureKeys";

// Team management page (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.5).
// Admin-only — gated by <TenantAdminRoute /> at the route level.
//
// Columns: Name, Email, Role, Status, Last Login, Assigned Clients,
// Actions. The BE list response carries `assigned_clients_count` per
// row as a 3-portal aggregate so we don't have to fan out (spec §5.8).
//
// Action handling:
//   - Change Role → PUT /users/{id}/role  body: {role}
//                   Last-admin demote → 409 LAST_ADMIN_PROTECTED → toast
//   - Disable / Enable → PUT /users/{id}/toggle-status body: {enabled}
//                   USER_HAS_ASSIGNMENTS on disable → open deactivation
//                                                     modal w/ counts
//                   LAST_ADMIN_PROTECTED on disable → toast
//   - Delete → DELETE /users/{id}
//                   Same two 409s as Disable.
//   - Resend Invite → POST /users/resend-invite/{id}  (only when
//                   activated === false)
//
// Pagination + search: uses URL search params so a refresh / share-link
// preserves the view. Search is debounced 300ms; pagination is whatever
// the BE's `page_size` came back as (we ask for 20).

const { Text, Paragraph } = Typography;

const PAGE_SIZE = 20;

// Portal-stats summary at the top of /team.
// Each entry hits `GET /api/v1/clients/<key>?page_size=1` (twice — once
// without filter for the org total, once with `assigned_user_id=unassigned`
// for the shared-pool count; assigned = total - shared). Color matches
// the accent we use for the same portal everywhere else in the app
// (geekblue / green / orange — see BulkAssignDialog). `userFlag` is the
// per-user portal-access field used to compute the row-level Shared
// Clients column (a user only "sees" the shared-pool count for portals
// they have access to, per USER_LEVEL_CLIENT_ASSIGNMENT.md §4.2).
const PORTAL_STATS_CONFIG = [
  {
    key: "income-tax",
    label: "Income Tax",
    color: "geekblue",
    userFlag: "enable_income_tax_portal",
    // Per-user assigned count returned by GET /api/v1/users (added
    // alongside the Shared Clients column rollout — see users.py).
    assignedField: "assigned_income_tax_count",
  },
  {
    key: "gst",
    label: "GST",
    color: "green",
    userFlag: "enable_gst_portal",
    assignedField: "assigned_gst_count",
  },
  {
    key: "tds",
    label: "TDS",
    color: "orange",
    userFlag: "enable_tds_portal",
    assignedField: "assigned_tds_count",
  },
];

// Roles the admin can switch a user to (spec §3.1). read_only is in the
// schema but UI-deferred per the FE handoff Q1.
const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "tenant_admin", label: "Admin" },
];

const TeamPage = () => {
  const { user: authUser } = useAuth();
  const { isEmbed } = useEmbedMode();
  const { claims } = useEmbedToken();
  const { refresh: refreshOrgUsersCache } = useOrgUsers();

  // Inside the embed iframe, AuthContext is empty (in-memory token only,
  // localStorage stays clean by design). Fall back to the SSO JWT
  // claims for the "who am I?" lookups (self-row protection at lines
  // ~330 and ~370). Outside the embed tree the SSO context is empty,
  // so authUser stays the source of truth.
  const currentUser = isEmbed
    ? claims
      ? { user_id: claims.sub, role: claims.role, email: claims.email }
      : null
    : authUser;
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") || "";
  const page = Number(searchParams.get("page") || "1");

  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-portal client-count summary (IT / GST / TDS). Each value is
  // { total, assigned, shared }. `null` while loading; never throws
  // since per-portal fetch failures fall back to zeros silently.
  const [portalStats, setPortalStats] = useState(null);
  const [portalStatsLoading, setPortalStatsLoading] = useState(true);

  // Per-row in-flight flag. Antd Table doesn't have native row-level
  // loading, so we keep a Set of user ids currently mid-action and
  // disable that row's buttons.
  const [pendingRowIds, setPendingRowIds] = useState(new Set());

  // Invite modal.
  const [inviteOpen, setInviteOpen] = useState(false);
  // Phase 4 — hide the Invite User button when MULTI_USER_SUPPORT is
  // not in the org's plan. Existing users continue to be listed (and
  // the user can edit themselves) so they don't lose access to their
  // own account. BE backstop on user-create returns 403 if a stale FE
  // still tries to invite.
  const multiUserEnabled = useFeatureEnabled(FEATURE_KEYS.MULTI_USER_SUPPORT);
  // Seat cap, supplied by the user-list response (server-owned limit).
  // null until the first load, so the button isn't disabled while unknown.
  const [maxUsers, setMaxUsers] = useState(null);
  const [orgUserCount, setOrgUserCount] = useState(null);
  const atUserCap =
    typeof maxUsers === "number" &&
    maxUsers > 0 &&
    typeof orgUserCount === "number" &&
    orgUserCount >= maxUsers;

  // Deactivation modal — opened when disable/delete returns 409
  // USER_HAS_ASSIGNMENTS. Carries the target user, the BE counts, and
  // a retry callback that re-runs whichever original action triggered
  // the modal.
  const [deactivation, setDeactivation] = useState(null);

  // Per-user portal-access modal (Feature 2). Holds the user row being
  // edited; null = closed. The modal posts to PUT /users/{id} with the
  // 3 enable_*_portal toggles.
  const [portalAccessUser, setPortalAccessUser] = useState(null);

  const queryParams = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch || undefined,
    }),
    [page, debouncedSearch]
  );

  // Sync debouncedSearch back into the URL so a Refresh/share-link works.
  useEffect(() => {
    if (debouncedSearch === search) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set("search", debouncedSearch);
    else next.delete("search");
    next.set("page", "1"); // any new search resets pagination
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchData(USERS.LIST, queryParams);
      // BE response: {users, total, page, page_size, total_pages}.
      // Some legacy envelopes wrap the body in {data: ...} — handle both.
      const body = raw?.data || raw || {};
      const items = Array.isArray(body.users)
        ? body.users
        : Array.isArray(body.items)
        ? body.items
        : [];
      setUsers(items);
      setTotal(typeof body.total === "number" ? body.total : items.length);
      // Seat cap comes FROM the server (MAX_USERS_PER_ORG) so the number is
      // never hard-coded here. `org_user_count` is the unfiltered org-wide
      // count — `total` reflects the active search and must not gate Invite.
      if (typeof body.max_users === "number") setMaxUsers(body.max_users);
      if (typeof body.org_user_count === "number")
        setOrgUserCount(body.org_user_count);
    } catch (err) {
      setError(err?.detail || err?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Org-level per-portal counts for the summary cards at the top of the
  // page. 2 lightweight calls per portal (total + shared with page_size=1),
  // 6 in parallel. Computed `assigned = total - shared` rather than a 3rd
  // call. Reuses the existing /api/v1/clients/<portal> list endpoint — no
  // new BE work. Failures land as zeros so the rest of the page still
  // renders cleanly even if one portal endpoint blips.
  const fetchPortalStats = useCallback(async () => {
    setPortalStatsLoading(true);
    const calls = PORTAL_STATS_CONFIG.flatMap((p) => [
      fetchData(`/api/v1/clients/${p.key}`, { page: 1, page_size: 1 }).catch(
        () => null,
      ),
      fetchData(`/api/v1/clients/${p.key}`, {
        page: 1,
        page_size: 1,
        assigned_user_id: "unassigned",
      }).catch(() => null),
    ]);
    const results = await Promise.all(calls);
    const next = {};
    PORTAL_STATS_CONFIG.forEach((p, i) => {
      const totalResp = results[i * 2];
      const sharedResp = results[i * 2 + 1];
      const total = totalResp?.total || 0;
      const shared = sharedResp?.total || 0;
      next[p.key] = { total, shared, assigned: total - shared };
    });
    setPortalStats(next);
    setPortalStatsLoading(false);
  }, []);

  useEffect(() => {
    fetchPortalStats();
  }, [fetchPortalStats]);

  const setPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  };

  const refreshAll = useCallback(() => {
    fetchUsers();
    fetchPortalStats();
    refreshOrgUsersCache();
  }, [fetchUsers, fetchPortalStats, refreshOrgUsersCache]);

  // Row-level pending tracking helpers.
  const startRowPending = (id) =>
    setPendingRowIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const endRowPending = (id) =>
    setPendingRowIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Generic 409 handler for the disable / delete flows. `actionLabel`
  // goes into the modal title ("Cannot disable…" / "Cannot delete…").
  // `retryAction` re-runs the original mutation after the deactivation
  // modal finishes its bulk-reassign loop.
  const handle409Conflict = (err, user, actionLabel, retryAction) => {
    if (is409WithCode(err, "USER_HAS_ASSIGNMENTS")) {
      const detail = get409Detail(err);
      setDeactivation({
        user,
        counts: detail?.counts || {},
        actionLabel,
        retryAction,
      });
      return true;
    }
    if (is409WithCode(err, "LAST_ADMIN_PROTECTED")) {
      const detail = get409Detail(err);
      antMessage.error(
        detail?.message ||
          `Cannot ${actionLabel.toLowerCase()} the last remaining admin.`
      );
      return true;
    }
    return false;
  };

  const handleRoleChange = async (userRow, newRole) => {
    if (newRole === userRow.role) return;
    startRowPending(userRow.id);
    try {
      await updateData(USERS.CHANGE_ROLE(userRow.id), { role: newRole });
      antMessage.success(
        `${userRow.email} is now ${newRole === "tenant_admin" ? "an admin" : "a regular user"}.`
      );
      refreshAll();
    } catch (err) {
      // LAST_ADMIN_PROTECTED on demote is the only structured 409 here
      // (USER_HAS_ASSIGNMENTS doesn't apply to role changes — assignments
      // survive the role swap). Generic errors fall through to a toast.
      if (is409WithCode(err, "LAST_ADMIN_PROTECTED")) {
        const detail = get409Detail(err);
        antMessage.error(
          detail?.message || "Cannot demote the last remaining admin."
        );
      } else {
        const msg = err?.detail || err?.message || "Failed to change role";
        antMessage.error(typeof msg === "string" ? msg : "Failed to change role");
      }
    } finally {
      endRowPending(userRow.id);
    }
  };

  const handleToggleStatus = (userRow) => {
    const targetEnabled = !userRow.enabled;
    const actionLabel = targetEnabled ? "Enable" : "Disable";
    Modal.confirm({
      title: `${actionLabel} ${userRow.email}?`,
      content: targetEnabled
        ? "The user will regain access to NoticeAI immediately."
        : "The user will lose access immediately. They keep their client assignments unless reassigned.",
      okText: actionLabel,
      okButtonProps: targetEnabled ? undefined : { danger: true },
      onOk: async () => {
        const run = async () => {
          await updateData(USERS.TOGGLE_STATUS(userRow.id), {
            enabled: targetEnabled,
          });
          antMessage.success(`${userRow.email} ${targetEnabled ? "enabled" : "disabled"}.`);
          refreshAll();
        };
        startRowPending(userRow.id);
        try {
          await run();
        } catch (err) {
          // Disable can return USER_HAS_ASSIGNMENTS (open deactivation
          // modal) or LAST_ADMIN_PROTECTED (toast). Enable returns
          // neither in practice (can't have orphan assignments while
          // disabled and reassignment didn't change), so fall through
          // to the generic toast.
          if (!targetEnabled && handle409Conflict(err, userRow, "Disable", run)) {
            return;
          }
          const msg = err?.detail || err?.message || `Failed to ${actionLabel.toLowerCase()}`;
          antMessage.error(typeof msg === "string" ? msg : `Failed to ${actionLabel.toLowerCase()}`);
        } finally {
          endRowPending(userRow.id);
        }
      },
    });
  };

  const handleDelete = (userRow) => {
    Modal.confirm({
      title: `Delete ${userRow.email}?`,
      content:
        "This permanently removes the user. Their assigned clients will be reassigned (or marked Shared) before the delete completes.",
      okText: "Delete",
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      onOk: async () => {
        const run = async () => {
          await deleteData(USERS.DELETE(userRow.id));
          antMessage.success(`${userRow.email} deleted.`);
          refreshAll();
        };
        startRowPending(userRow.id);
        try {
          await run();
        } catch (err) {
          if (handle409Conflict(err, userRow, "Delete", run)) return;
          const msg = err?.detail || err?.message || "Failed to delete";
          antMessage.error(typeof msg === "string" ? msg : "Failed to delete");
        } finally {
          endRowPending(userRow.id);
        }
      },
    });
  };

  const handleResendInvite = async (userRow) => {
    startRowPending(userRow.id);
    try {
      await postResponseData(USERS.RESEND_INVITE(userRow.id), {});
      antMessage.success(`Invite re-sent to ${userRow.email}.`);
    } catch (err) {
      const msg = err?.detail || err?.message || "Failed to resend invite";
      antMessage.error(typeof msg === "string" ? msg : "Failed to resend invite");
    } finally {
      endRowPending(userRow.id);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "full_name",
        key: "full_name",
        render: (name, row) => name || row.email || "-",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        render: (role, row) => {
          const isSelf = row.id === currentUser?.user_id;
          return (
            <Select
              value={role}
              options={ROLE_OPTIONS}
              size="small"
              style={{ minWidth: 130 }}
              disabled={pendingRowIds.has(row.id) || isSelf}
              onChange={(next) => handleRoleChange(row, next)}
            />
          );
        },
      },
      {
        title: "Status",
        dataIndex: "enabled",
        key: "enabled",
        render: (enabled, row) => {
          if (row.activated === false) return <Tag color="orange">Pending invite</Tag>;
          if (!enabled) return <Tag color="red">Disabled</Tag>;
          return <Tag color="green">Active</Tag>;
        },
      },
      {
        title: "Last Login",
        dataIndex: "last_login",
        key: "last_login",
        render: (iso) => formatDate(iso, true),
      },
      {
        // Assigned Clients = clients with this user's id in
        // assigned_user_id. BE returns the aggregate
        // `assigned_clients_count` plus per-portal split fields
        // `assigned_income_tax_count` / `assigned_gst_count` /
        // `assigned_tds_count` (added 20-05-2026). Render mirrors the
        // Shared Clients column: bold total + per-portal mini-tags.
        // Portals the user has no access to are dimmed + show "—".
        title: "Assigned Clients",
        key: "assigned_clients_count",
        align: "right",
        render: (_, row) => {
          const breakdown = PORTAL_STATS_CONFIG.map((p) => {
            const accessible = row[p.userFlag] !== false;
            const count = accessible ? row[p.assignedField] ?? 0 : 0;
            return { key: p.key, label: p.label, color: p.color, count, accessible };
          });
          const total = row.assigned_clients_count ?? breakdown.reduce(
            (s, b) => s + b.count, 0,
          );
          return (
            <div style={{ textAlign: "right", lineHeight: 1.2 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{total}</div>
              <div
                style={{
                  fontSize: 11,
                  color: "#8c8c8c",
                  marginTop: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {breakdown.map((b, i) => (
                  <span key={b.key} style={{ marginLeft: i ? 6 : 0 }}>
                    <Tag
                      color={b.accessible ? b.color : "default"}
                      style={{
                        margin: 0,
                        padding: "0 4px",
                        fontSize: 10,
                        lineHeight: "16px",
                      }}
                    >
                      {b.label === "Income Tax" ? "IT" : b.label}
                    </Tag>{" "}
                    {b.accessible ? b.count : "-"}
                  </span>
                ))}
              </div>
            </div>
          );
        },
      },
      {
        // Shared Clients = number of clients in the org's shared pool
        // (assigned_user_id IS NULL) that THIS row's user can see —
        // per USER_LEVEL_CLIENT_ASSIGNMENT.md §4.2 the shared visibility
        // is gated by the user's per-portal access flags. So the count
        // is the sum of per-portal shared counts across the portals the
        // user has enabled. Computed FE-side from portalStats (loaded
        // once at mount) + the user-row flags — no per-row BE call.
        //
        // Render shows the aggregate prominently with a per-portal
        // breakdown beneath in small text — admin can see at a glance
        // which portals contribute. Portals the user has no access to
        // are dimmed and labelled "no access".
        title: "Shared Clients",
        key: "shared_clients_count",
        align: "right",
        render: (_, row) => {
          if (!portalStats) return "-";
          const breakdown = PORTAL_STATS_CONFIG.map((p) => {
            const accessible = row[p.userFlag] !== false;
            const count = accessible ? portalStats[p.key]?.shared ?? 0 : 0;
            return { key: p.key, label: p.label, color: p.color, count, accessible };
          });
          const total = breakdown.reduce((s, b) => s + b.count, 0);
          return (
            <div style={{ textAlign: "right", lineHeight: 1.2 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{total}</div>
              <div
                style={{
                  fontSize: 11,
                  color: "#8c8c8c",
                  marginTop: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {breakdown.map((b, i) => (
                  <span key={b.key} style={{ marginLeft: i ? 6 : 0 }}>
                    <Tag
                      color={b.accessible ? b.color : "default"}
                      style={{
                        margin: 0,
                        padding: "0 4px",
                        fontSize: 10,
                        lineHeight: "16px",
                      }}
                    >
                      {b.label === "Income Tax" ? "IT" : b.label}
                    </Tag>{" "}
                    {b.accessible ? b.count : "-"}
                  </span>
                ))}
              </div>
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "_actions",
        render: (_, row) => {
          const isSelf = row.id === currentUser?.user_id;
          const pending = pendingRowIds.has(row.id);
          return (
            <Space size="small" wrap>
              {row.activated === false && row.enabled && (
                <Tooltip title="Re-send the invite email with a fresh 7-day token">
                  <Button
                    size="small"
                    icon={<MailOutlined />}
                    loading={pending}
                    onClick={() => handleResendInvite(row)}
                  >
                    Resend
                  </Button>
                </Tooltip>
              )}
              {!isSelf && (
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  danger={row.enabled}
                  loading={pending}
                  onClick={() => handleToggleStatus(row)}
                >
                  {row.enabled ? "Disable" : "Enable"}
                </Button>
              )}
              {!isSelf && (
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  loading={pending}
                  onClick={() => handleDelete(row)}
                >
                  Delete
                </Button>
              )}
              {!isSelf
                && row.role !== "super_admin"
                && row.role !== "partner_admin" && (
                <Button
                  size="small"
                  onClick={() => setPortalAccessUser(row)}
                >
                  Portals
                </Button>
              )}
              {isSelf && (
                <Tooltip title="You cannot disable, delete, or change your own role from here.">
                  <Tag>Yourself</Tag>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.user_id, pendingRowIds, portalStats]
  );

  return (
    <>
      {/* Top row: Breadcrumb left + Dashboard button right.
          Mirrors Eproceedings.jsx layout so /team feels consistent with
          IT/GST/TDS notice-list pages — no outer max-width wrapper, full
          page width like the reference. */}
      <div className="flex w-full items-center justify-between">
        <Breadcrumb
          items={[
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{ pathname: "/" }}
                >
                  Home
                </TenantLink>
              ),
            },
            { title: "Users" },
          ]}
          style={{ marginBottom: "10px", color: "#151c40" }}
        />
        {/* Dashboard button — hidden in embed mode because the embed host
            (Partner Simulator) owns its own dashboard and the iframe has
            no NoticeAI "/" to land on. Matches DashboardButton's embed
            guard for IT/GST/TDS pages. */}
        {!isEmbed && (
          <TenantLink
            to={{ pathname: "/" }}
            className="group flex items-center gap-3 px-4 py-3 rounded-lg
                       bg-gray-50/50 hover:bg-indigo-50
                       border border-gray-200/50 hover:border-indigo-200
                       text-gray-600 hover:text-indigo-700
                       transition-all duration-200 ease-out
                       hover:shadow-sm exempt-from-readonly mb-4"
          >
            <HomeIcon className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors duration-200" />
            <span className="font-medium text-sm">Dashboard</span>
          </TenantLink>
        )}
      </div>

      {/* Blue banner with back arrow. PageTitle uses navigate(-1) which is
          browser/iframe history back — works in both simple and embed modes
          because the iframe maintains its own history stack. */}
      <PageTitle title="Users" />

      {/* Action buttons row — sits directly under PageTitle banner with
          no horizontal inset, matching Eproceedings.jsx flow. */}
      <div className="flex flex-wrap gap-2 mt-4 items-center">
        <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>
          Refresh
        </Button>
        {multiUserEnabled && (
          <Tooltip
            title={
              atUserCap
                ? `User limit reached (${orgUserCount} of ${maxUsers}). Delete an existing user to invite another.`
                : ""
            }
          >
            {/* span wrapper so the tooltip still fires on a disabled button */}
            <span>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => setInviteOpen(true)}
                disabled={atUserCap}
              >
                Invite User
              </Button>
            </span>
          </Tooltip>
        )}
        {multiUserEnabled && typeof maxUsers === "number" && maxUsers > 0 && (
          <span className="text-slate-500 text-sm">
            {orgUserCount} of {maxUsers} users used
          </span>
        )}
      </div>

      {/* Description kept (not in screenshot 2 but useful context). */}
      <div className="mt-2 mb-4">
        <Text type="secondary">
          Invite users, change roles, manage access. Each user sees only the
          clients assigned to them or marked Shared.
        </Text>
      </div>

      {/* Org-level per-portal client-count summary cards — Total / Assigned
          / Shared per portal (IT / GST / TDS). Commented out per user's
          ask on 2026-05-20: the per-user "Shared Clients" table column
          covers the per-row use case and the 3-card header was deemed
          redundant. Kept here (not deleted) because the underlying data
          fetch (`portalStats`) still powers the Shared Clients column —
          re-enabling these cards is a 1-block uncomment if needed later.
      <Row gutter={[16, 16]} className="mb-4">
        {PORTAL_STATS_CONFIG.map((p) => {
          const s = portalStats?.[p.key];
          return (
            <Col xs={24} sm={12} md={8} key={p.key}>
              <Card
                size="small"
                loading={portalStatsLoading}
                title={
                  <Space>
                    <Tag color={p.color}>{p.label}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Clients
                    </Text>
                  </Space>
                }
              >
                <Row gutter={8}>
                  <Col span={8}>
                    <Statistic
                      title="Total"
                      value={s?.total ?? 0}
                      valueStyle={{ fontSize: 20, fontWeight: 600 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Assigned"
                      value={s?.assigned ?? 0}
                      valueStyle={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: (s?.assigned ?? 0) > 0 ? "#1677ff" : "#8c8c8c",
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Shared"
                      value={s?.shared ?? 0}
                      valueStyle={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: (s?.shared ?? 0) > 0 ? "#52c41a" : "#8c8c8c",
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          );
        })}
      </Row>
      */}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input.Search
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          style={{ maxWidth: 360 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load users"
          description={error}
        />
      ) : !loading && users.length === 0 ? (
        <Empty
          description={
            search
              ? `No users match "${search}".`
              : "Your users list is empty. Invite a user to get started."
          }
        />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          dataSource={users}
          columns={columns}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      )}

      <InviteUserModal
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onSuccess={() => {
          setInviteOpen(false);
          refreshAll();
        }}
      />

      <UserDeactivationModal
        open={Boolean(deactivation)}
        user={deactivation?.user}
        counts={deactivation?.counts}
        actionLabel={deactivation?.actionLabel || "Disable"}
        retryAction={deactivation?.retryAction || (() => Promise.resolve())}
        onCancel={() => setDeactivation(null)}
        onSuccess={() => {
          setDeactivation(null);
          refreshAll();
        }}
      />

      <PortalAccessModal
        user={portalAccessUser}
        onClose={() => setPortalAccessUser(null)}
        onSaved={() => {
          setPortalAccessUser(null);
          refreshAll();
        }}
      />
    </>
  );
};

/**
 * Inline portal-access editor for tenant_admin (Feature 2). Mounted
 * inside TeamPage so it shares the page's auth + refresh behaviour.
 * Stays as a small Modal rather than a full Drawer because it's
 * single-purpose: a handful of switches + a save button.
 *
 * Two groups (see PortalAccessFields):
 *   Portals      - Income Tax / GST / TDS
 *   Capabilities - per portal [2026-07-15]: Response Generation (IT/GST/TDS),
 *                  Prepare Appeal (IT/GST), and All Client Access for
 *                  Prepare Appeal (IT/GST).
 */
const PortalAccessModal = ({ user, onClose, onSaved }) => {
  const [val, setVal] = useState({
    enable_income_tax_portal: true,
    enable_gst_portal: true,
    enable_tds_portal: true,
    // Per-portal capabilities (2026-07-15).
    enable_response_generation_income_tax: true,
    enable_response_generation_gst: true,
    enable_response_generation_tds: true,
    enable_prepare_appeal_income_tax: true,
    enable_prepare_appeal_gst: true,
    enable_prepare_appeal_all_clients_income_tax: false,
    enable_prepare_appeal_all_clients_gst: false,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) {
      setVal({
        enable_income_tax_portal: user.enable_income_tax_portal ?? true,
        enable_gst_portal: user.enable_gst_portal ?? true,
        enable_tds_portal: user.enable_tds_portal ?? true,
        enable_response_generation_income_tax:
          user.enable_response_generation_income_tax ?? true,
        enable_response_generation_gst:
          user.enable_response_generation_gst ?? true,
        enable_response_generation_tds:
          user.enable_response_generation_tds ?? true,
        enable_prepare_appeal_income_tax:
          user.enable_prepare_appeal_income_tax ?? true,
        enable_prepare_appeal_gst: user.enable_prepare_appeal_gst ?? true,
        enable_prepare_appeal_all_clients_income_tax:
          user.enable_prepare_appeal_all_clients_income_tax ?? false,
        enable_prepare_appeal_all_clients_gst:
          user.enable_prepare_appeal_all_clients_gst ?? false,
      });
    }
  }, [user]);
  if (!user) return null;
  const save = async () => {
    setSaving(true);
    try {
      await updateData(USERS.UPDATE(user.id), val);
      antMessage.success(`Portal access updated for ${user.email}.`);
      onSaved();
    } catch (err) {
      antMessage.error(typeof err === "string" ? err : err?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={!!user}
      title="Portal access"
      okText="Save"
      onCancel={() => !saving && onClose()}
      onOk={save}
      confirmLoading={saving}
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Switch off any portal or capability you don&apos;t want this user to
        access. Your organization&apos;s subscription gates what is available
        overall; these switches narrow it further for this user.
      </Paragraph>
      <PortalAccessFields value={val} onChange={setVal} layout="vertical" />
    </Modal>
  );
};

export default TeamPage;
